import { useEffect, useId, useMemo, useState, type ChangeEvent } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { getLanguage } from '@/constants/languages'
import {
  clampToMaxLength,
  MEETING_FEEDBACK_COMMENT_MAX_LENGTH,
} from '@/constants/meeting'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { XMarkIcon } from '@/components/Icons'
import {
  createMeetingFeedbackSchema,
  type MeetingFeedbackFormValues,
} from './meeting-feedback.schema'

export interface MeetingFeedbackSummary {
  title: string
  sourceLang: string
  targetLang: string
  durationMinutes: number
}

interface MeetingFeedbackModalProps {
  meeting: MeetingFeedbackSummary
  isSubmitting: boolean
  error?: string | null
  onSubmit: (payload: { rating: number; comment: string }) => void
  /**
   * `'create'` (default) is the first-submission flow triggered from
   * `MeetingChat` when a meeting ends. `'edit'` is used from the meeting
   * detail page to update feedback that was already submitted — it
   * pre-fills the form. Both modes render a Cancel affordance whenever
   * `onClose` is supplied.
   */
  mode?: 'create' | 'edit'
  /** Rating to pre-fill when `mode` is `'edit'`. */
  initialRating?: number
  /** Comment to pre-fill when `mode` is `'edit'`. */
  initialComment?: string
  /**
   * Closes the modal without submitting feedback. In `edit` mode this
   * discards an in-progress edit. In `create` mode (passed by `MeetingChat`)
   * this instead resumes the just-ended meeting, since ending it hasn't
   * actually been persisted yet at this point — the caller decides what
   * "cancel" means for its own flow. Renders the Cancel button whenever set.
   */
  onClose?: () => void
}

export default function MeetingFeedbackModal({
  meeting,
  isSubmitting,
  error,
  onSubmit,
  mode = 'create',
  initialRating = 0,
  initialComment = '',
  onClose,
}: MeetingFeedbackModalProps) {
  const { t } = useUiStrings()
  const commentsId = useId()
  const ratingId = useId()
  const [hoverRating, setHoverRating] = useState(0)
  const isEditMode = mode === 'edit'

  const schema = useMemo(
    () =>
      createMeetingFeedbackSchema(
        fmt(t.fieldTooLong, { max: MEETING_FEEDBACK_COMMENT_MAX_LENGTH })
      ),
    [t.fieldTooLong]
  )

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<MeetingFeedbackFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      rating: initialRating,
      comment: clampToMaxLength(initialComment, MEETING_FEEDBACK_COMMENT_MAX_LENGTH),
    },
  })

  const rating = watch('rating')
  const comment = watch('comment')
  const displayRating = hoverRating || rating
  // Both fields stay optional — feedback can still be skipped entirely — but
  // either one on its own is already something worth sending, so the button
  // says Submit as soon as there is anything to submit (BRAI-572).
  //
  // It used to require both, which told somebody who had just picked a star
  // that their rating was about to be thrown away. It never was: the form
  // submits whatever is filled in either way, so only the label was wrong.
  const canSubmitFeedback = rating > 0 || comment.trim().length > 0

  const sourceLanguage = getLanguage(meeting.sourceLang)
  const targetLanguage = getLanguage(meeting.targetLang)

  function formatDuration(minutes: number): string {
    if (minutes < 1) return t.durationLessThanMin
    return `${minutes} ${minutes === 1 ? t.minute : t.minutes}`
  }

  useEffect(() => {
    if (error) {
      setError('root', { message: error })
    }
  }, [error, setError])

  const onFormSubmit = (data: MeetingFeedbackFormValues) => {
    clearErrors('root')
    onSubmit({ rating: data.rating, comment: data.comment.trim() })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/30 px-3 py-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="meeting-feedback-title"
    >
      <div className="animate-scale-in relative max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 shadow-2xl sm:p-6 md:p-8">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label={t.close}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-50"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-[var(--accent)] dark:bg-[var(--accent-light)]">
            <ChatBubbleIcon className="h-6 w-6" />
          </div>
          <h2
            id="meeting-feedback-title"
            className="mb-1 text-lg font-bold text-[var(--text-primary)]"
          >
            {isEditMode ? t.yourFeedback : t.howWasMeeting}
          </h2>
          <p className="text-sm text-[var(--text-secondary)]">{t.feedbackSubtitle}</p>
        </div>

        <div className="mb-5 flex items-center gap-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-blue-100 text-[var(--accent)]">
            <CalendarIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[var(--text-primary)]">
              {meeting.title}
            </p>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              {formatDuration(meeting.durationMinutes)} • {sourceLanguage.name} →{' '}
              {targetLanguage.name}
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
            {t.completed}
          </span>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4" noValidate>
          <div>
            <label
              id={ratingId}
              className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
            >
              {t.translationQuality}
            </label>
            <div role="radiogroup" aria-labelledby={ratingId} className="flex items-center gap-2">
              {Array.from({ length: 5 }, (_, index) => {
                const starValue = index + 1
                const filled = starValue <= displayRating
                return (
                  <button
                    key={starValue}
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => {
                      setValue('rating', starValue, { shouldValidate: true })
                      clearErrors('root')
                    }}
                    onMouseEnter={() => setHoverRating(starValue)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="rounded-md p-1 transition-transform hover:scale-110 disabled:opacity-50"
                    aria-label={fmt(t.rateStar, { n: starValue })}
                  >
                    <StarIcon filled={filled} />
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <label
              htmlFor={commentsId}
              className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
            >
              {t.additionalComments}
            </label>
            <textarea
              id={commentsId}
              rows={4}
              placeholder={t.feedbackPlaceholder}
              disabled={isSubmitting}
              maxLength={MEETING_FEEDBACK_COMMENT_MAX_LENGTH}
              className="input-field min-h-[96px] resize-none"
              {...register('comment', {
                onChange: (event: ChangeEvent<HTMLTextAreaElement>) => {
                  const next = clampToMaxLength(
                    event.target.value,
                    MEETING_FEEDBACK_COMMENT_MAX_LENGTH
                  )
                  if (next !== event.target.value) {
                    event.target.value = next
                  }
                  clearErrors('root')
                },
              })}
            />
            <p className="mt-1 text-right text-[10px] font-semibold text-slate-600 dark:text-[var(--text-tertiary)]">
              {comment.length} / {MEETING_FEEDBACK_COMMENT_MAX_LENGTH}
            </p>
            {errors.comment && (
              <p className="mt-1.5 text-xs text-[var(--error)]" role="alert">
                {errors.comment.message}
              </p>
            )}
          </div>

          {errors.root && (
            <p className="text-sm text-[var(--error)]" role="alert">
              {errors.root.message}
            </p>
          )}

          <div className={onClose ? 'flex gap-3' : ''}>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 rounded-lg border border-[var(--border-color)] px-4 py-2 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
              >
                {t.cancel}
              </button>
            )}
            <button
              type="submit"
              className={
                onClose
                  ? 'btn-primary flex flex-1 items-center justify-center gap-2 disabled:pointer-events-none disabled:opacity-50'
                  : 'btn-primary flex w-full items-center justify-center gap-2 disabled:pointer-events-none disabled:opacity-50'
              }
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t.saving
                : isEditMode
                  ? t.saveChanges
                  : canSubmitFeedback
                    ? t.submitFeedback
                    : t.skipFeedback}
              {!isSubmitting && <SendIcon className="h-4 w-4" />}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`h-7 w-7 ${filled ? 'text-amber-400' : 'text-slate-300'}`}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3.5 14.8 9l6.2.9-4.5 4.4 1.1 6.2L12 17.8 6.4 20.5l1.1-6.2L3 9.9 9.2 9 12 3.5Z"
      />
    </svg>
  )
}

function ChatBubbleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M4 5a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H9l-4 3v-3H7a3 3 0 0 1-3-3V5Z" />
    </svg>
  )
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 2v4m8-4v4M4 10h16M6 5h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
      />
    </svg>
  )
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M3.4 20.6 21 12 3.4 3.4l2.8 7.2L16 12l-9.8 1.4-2.8 7.2Z" />
    </svg>
  )
}
